import { useApi } from '@/api';
import { TableSkeleton, SelectFilter } from '@/components';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import {
  NSC_URN_OPERATIONS,
  PSM_URN_OPERATIONS,
  LITE_PSM_URN_OPERATIONS
} from '@/constants';
import {
  CryptoIcon,
  CurrencyValue,
  EtherScanPictogram,
  Flex,
  Heading,
  useTableProps,
  DataTable,
  RelativeDateTime
} from '@blockanalitica/ui';
import { useMemo } from 'react';

export default function EventsTable({ ilk }) {
  const { pagination, sorting, filters } = useTableProps({
    pageSize: 10,
    initialSorting: '-debt',
    filtersDebounceTime: 50
  });

  const [data, error] = useApi({
    url: `/groups/stablecoins/ilks/${ilk}/events/`,
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      order: sorting.currentSorting,
      operation: filters.filtersApplied.eventType
    }
  });

  if (error) {
    throw error;
  }

  const mapping = useMemo(() => {
    if (['PSM-GUSD-A', 'PSM-PAX-A', 'PSM-USDC-A'].includes(ilk)) {
      return PSM_URN_OPERATIONS;
    } else if (ilk === 'LITE-PSM-USDC-A') {
      return LITE_PSM_URN_OPERATIONS;
    } else {
      return NSC_URN_OPERATIONS;
    }
  }, [ilk]);

  const selectFilterOptions = useMemo(() => {
    return [
      { label: 'All events', value: '' },
      ...Object.entries(mapping).map(([key, value]) => ({
        label: value,
        value: key
      }))
    ];
  }, [mapping]);

  if (data) {
    const columns: TableColumn[] = [
      {
        header: 'Event',
        getCell: (row) => row.operation,
        columnSize: ['minmax(100px, auto)']
      },
      {
        header: (
          <Flex
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="3xs">
            Debt
            <CryptoIcon name="DAI" size="0" />
          </Flex>
        ),
        getCell: (row) => (
          <CurrencyValue
            value={row.dart}
            options={{
              nullable: null,
              trend: true,
              iconSize: '0'
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'dart'
      },
      {
        header: (
          <Flex
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="3xs">
            Collateral
            <CryptoIcon
              name={data.collateral_symbol}
              address={data.collateral_address}
              size="0"
            />
          </Flex>
        ),
        getCell: (row) => (
          <CurrencyValue
            value={row.dink}
            options={{
              trend: true,
              nullable: null,
              iconSize: '0'
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'dink'
      },
      {
        header: 'Time',
        getCell: (row) => (
          <Flex gap={['xs', '3xs']} color="muted">
            <RelativeDateTime value={row.datetime} />
            <a
              href={`https://etherscan.io/tx/${row.tx_hash}`}
              target="_blank"
              rel="noreferrer">
              <EtherScanPictogram size="0" />
            </a>
          </Flex>
        ),
        sortingField: 'order_index',
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      }
    ];

    return (
      <>
        <Flex justifyContent="space-between" alignItems="center">
          <Heading size="3">Events</Heading>
          <SelectFilter
            key="event-filter"
            filterField="eventType"
            options={selectFilterOptions}
            pagination={pagination}
            initialValue={filters.filtersApplied.eventType || ''}
            {...filters}
          />
        </Flex>
        <DataTable
          rows={data['results']}
          columns={columns}
          getKey={(row) => row.order_index}
          sorting={sorting}
          pagination={{
            ...pagination,
            totalRecords: data.count
          }}
        />
      </>
    );
  }
  return <TableSkeleton />;
}
